/**
 * Locale Priority Resolution - Shared Module
 * 
 * Implements the 4-tier locale priority chain for resolving slug collisions:
 * 1. STORED  → localStorage 'user-locale'
 * 2. BROWSER → navigator.languages preference
 * 3. EN      → English fallback (source locale)
 * 4. FIRST   → First available row
 * 
 * Used by both useSlugResolver and useTwoSegmentResolver
 * to resolve slug collisions across locales.
 */

import { USER_LOCALE_STORAGE_KEY } from '@/constants/routing';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Generic type constraint for rows that have a locale field
 */
export interface LocaleRow {
  locale: string;
}

// ============================================================================
// LOCALE PRIORITY HELPERS
// ============================================================================

/**
 * Get the user's preferred locale from localStorage
 */
export function getPreferredLocale(): string {
  try {
    return localStorage.getItem(USER_LOCALE_STORAGE_KEY) || 'en-US';
  } catch {
    return 'en-US';
  }
}

/**
 * Get browser language preferences
 */
export function getBrowserLanguages(): string[] {
  if (typeof navigator === 'undefined') return ['en-US'];
  return Array.from(navigator.languages || [navigator.language]).filter(Boolean);
}

/**
 * Options for locale priority selection
 */
export interface LocalePriorityOptions {
  /** 
   * Preferred locale hint (e.g., location's main_language).
   * Inserted as priority 2.5: after stored locale, before browser preferences.
   */
  preferLocale?: string;
}

/**
 * Select the best row based on locale priority:
 * 1. Stored locale (localStorage) - exact match
 * 2. Stored locale - base language match
 * 2.5. preferLocale hint - exact or base match (e.g., location main_language)
 * 3. Browser preferences - exact match
 * 4. Browser preferences - base language match
 * 5. English (en or en-*)
 * 6. First available
 * 
 * @param rows - Array of rows with locale field (must have at least 1 row)
 * @param options - Optional configuration including preferLocale hint
 * @returns The row that best matches the user's locale preferences
 */
export function selectByLocalePriority<T extends LocaleRow>(rows: T[], options?: LocalePriorityOptions): T {
  if (rows.length === 1) return rows[0];

  // 1. Check stored locale (exact match)
  const storedLocale = getPreferredLocale();
  const storedMatch = rows.find(r => r.locale === storedLocale);
  if (storedMatch) return storedMatch;
  
  // 2. Check stored locale (base language match)
  const storedBase = storedLocale.split('-')[0];
  const storedBaseMatch = rows.find(r => r.locale.startsWith(storedBase));
  if (storedBaseMatch) return storedBaseMatch;

  // 2.5. Check preferLocale hint (e.g., location's main_language)
  if (options?.preferLocale) {
    const pref = options.preferLocale;
    const prefExact = rows.find(r => r.locale === pref);
    if (prefExact) return prefExact;
    const prefBase = pref.split('-')[0];
    const prefBaseMatch = rows.find(r => r.locale.startsWith(prefBase));
    if (prefBaseMatch) return prefBaseMatch;
  }

  // 3-4. Check browser preferences
  const browserLangs = getBrowserLanguages();
  for (const browserLang of browserLangs) {
    // Exact match
    const exactMatch = rows.find(r => r.locale === browserLang);
    if (exactMatch) return exactMatch;
    
    // Base language match
    const baseMatch = rows.find(r => r.locale.startsWith(browserLang.split('-')[0]));
    if (baseMatch) return baseMatch;
  }

  // 5. English fallback
  const englishMatch = rows.find(r => r.locale.startsWith('en'));
  if (englishMatch) return englishMatch;

  // 6. First available
  return rows[0];
}
